import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Product } from '../types/types';
import { PRODUCT_CATEGORIES, PET_TYPES } from '../data/constants';
import dataService from '../data/dataService';
import ProductCard from '../components/ProductCard';
import SearchIcon from '../components/icons/SearchIcon';
import '../styles/Products.css';

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const petType = searchParams.get('petType') || '';
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const tagsFilter = searchParams.get('tags') || '';

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      const data = await dataService.getProducts();
      setProducts(data);
      setLoading(false);
    };
    loadProducts();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesPetType = !petType || product.petType === petType;
    const matchesCategory = !category || product.category === category;
    const matchesSearch =
      !search ||
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      product.description.toLowerCase().includes(search.toLowerCase());
    const matchesTags = !tagsFilter || product.tags?.some(t => t === tagsFilter);
    return matchesPetType && matchesCategory && matchesSearch && matchesTags;
  });

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-header">
        <h1>Products</h1>
        <p className="results-count">{filteredProducts.length} products found</p>
      </div>

      {/* Search */}
      <div className="search-bar">
        <SearchIcon />
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={e => updateFilter('search', e.target.value)}
        />
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Pet Type</label>
          <select
            value={petType}
            onChange={e => updateFilter('petType', e.target.value)}
          >
            <option value="">All Pets</option>
            {PET_TYPES.filter(p => p.value !== 'All').map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Category</label>
          <select
            value={category}
            onChange={e => updateFilter('category', e.target.value)}
          >
            <option value="">All Categories</option>
            {PRODUCT_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
            ))}
          </select>
        </div>
        <button
          className={`btn btn-sm ${tagsFilter === 'contoso' ? 'btn-contoso-active' : 'btn-contoso'}`}
          onClick={() => updateFilter('tags', tagsFilter === 'contoso' ? '' : 'contoso')}
        >
          🏷️ Contoso Collection
        </button>
        {(petType || category || search || tagsFilter) && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setSearchParams({})}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Results */}
      {filteredProducts.length > 0 ? (
        <div className="products-results">
          {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="no-results">
          <span className="no-results-icon">🔍</span>
          <h3>No products found</h3>
          <p>Try adjusting your filters or search term.</p>
          <button className="btn btn-primary" onClick={() => setSearchParams({})}>
            Clear All Filters
          </button>
        </div>
      )}


    </div>
  );
};

export default Products;
